import React from 'react';
import Animated from "react-native-web/dist/exports/Animated";
import Platform from "react-native-web/dist/exports/Platform";
import View from "react-native-web/dist/exports/View";
import Text from "react-native-web/dist/exports/Text";
import StyleSheet from "react-native-web/dist/exports/StyleSheet";
import ScrollView from "react-native-web/dist/exports/ScrollView";
import PropTypes from 'prop-types';
import ABBPropTypes from 'airbnb-prop-types';
import ExpandCollapseControl from './Control';
import { useThemeTokens } from '../ThemeProvider';
import { a11yProps, getTokensPropType, selectSystemProps, useVerticalExpandAnimation, variantProp, viewProps, useCopy } from '../utils';
import dictionary from './dictionary';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const [selectProps, selectedSystemPropTypes] = selectSystemProps([a11yProps, viewProps]);

// just void functions for now, since there are no style tokens for a panel or control at this point
const selectContainerStyles = _ref => {
  let {
    contentPaddingLeft,
    contentPaddingRight,
    contentPaddingTop,
    contentPaddingBottom
  } = _ref;
  return {
    paddingLeft: contentPaddingLeft,
    paddingRight: contentPaddingRight,
    paddingTop: contentPaddingTop,
    paddingBottom: contentPaddingBottom,
    flex: 1
  };
};
const selectTextStyles = _ref2 => {
  let {
    contentPanelFontSize,
    contentPanelFontName,
    contentPanelFontColor,
    contentPanelFontWeight,
    contentPanelLineHeight
  } = _ref2;
  return {
    fontSize: contentPanelFontSize,
    fontFamily: `${contentPanelFontName}${contentPanelFontWeight}normal`,
    lineHeight: contentPanelFontSize * contentPanelLineHeight,
    color: contentPanelFontColor
  };
};
const selectContentPanelStyles = _ref3 => {
  let {
    contentPanelBackgroundColor,
    contentPanelPaddingTop,
    contentPanelPaddingBottom,
    contentPanelPaddingLeft,
    contentPanelPaddingRight,
    contentPanelBorderWidth,
    contentPanelBorderColor,
    borderRadius,
    marginBottom
  } = _ref3;
  return {
    backgroundColor: contentPanelBackgroundColor,
    paddingTop: contentPanelPaddingTop,
    paddingBottom: contentPanelPaddingBottom,
    paddingLeft: contentPanelPaddingLeft,
    paddingRight: contentPanelPaddingRight,
    borderWidth: contentPanelBorderWidth,
    borderColor: contentPanelBorderColor,
    borderStyle: 'solid',
    borderRadius,
    marginBottom
  };
};
const selectControlPanelStyles = _ref4 => {
  let {
    contentPanelBackgroundColor
  } = _ref4;
  return {
    backgroundColor: contentPanelBackgroundColor
  };
};

/**
 * An item in an `ExpandCollapse` which contains collapsible `children` and a `control` that opens
 * and closes the collapsible children when pressed.
 *
 * Expects `openIds` and `onToggle` props from the `ExpandCollapse` render function and a unique
 * `panelId` that matches the ids passed to the `<ExpandCollapse>`.
 *
 * The panel does not need to be a direct child of the `<ExpandCollapse>` (unless this is required
 * by the chosen accessibility props for a particular accessibility tools).
 */
const ExpandCollapsePanel = /*#__PURE__*/React.forwardRef((_ref5, ref) => {
  let {
    openIds = [],
    panelId,
    onToggle,
    onPress,
    control,
    controlTokens,
    children,
    tokens,
    variant,
    controlRef,
    content,
    copy = 'en',
    ...rest
  } = _ref5;
  const [containerHeight, setContainerHeight] = React.useState(null);
  const isExpanded = openIds.includes(panelId);
  const getCopy = useCopy({
    dictionary,
    copy
  });
  const selectedProps = selectProps({
    ...rest,
    accessibilityState: {
      ...(rest.accessibilityState || {}),
      expanded: isExpanded
    }
  });
  const themeTokens = useThemeTokens('ExpandCollapsePanel', tokens, variant, {
    expanded: isExpanded
  });

  // on mobile devices we require a scroll buffer equal to the font size
  // to avoid triggering scrolling unnecessarily
  const mobileScrollBuffer = Platform.OS === 'web' ? 0 : selectTextStyles(themeTokens)?.fontSize;
  const handleControlPress = event => {
    onToggle?.(panelId, event);
    if (onPress) onPress(panelId, event);
  };
  const onContainerLayout = event => {
    const {
      layout: {
        height = 0
      } = {}
    } = event.nativeEvent;
    if (Platform.OS === 'web' || Platform.OS !== 'web' && containerHeight === null) {
      setContainerHeight(height + mobileScrollBuffer);
    }
  };
  const [animatedStyles, animatedRef] = useVerticalExpandAnimation({
    containerHeight,
    isExpanded,
    tokens: themeTokens
  });
  const focusabilityProps = isExpanded ? {} : a11yProps.nonFocusableProps;
  const panelAccessibilityLabel = `${panelId} ${getCopy('panel')}`;
  const subPanelAccessibilityLabel = `${panelId} ${getCopy('subPanel')}`;
  return content ? /*#__PURE__*/_jsx(View, {
    style: [selectContentPanelStyles(themeTokens), staticStyles.panelContainer],
    accessibilityLabel: panelAccessibilityLabel,
    children: typeof children === 'string' ? /*#__PURE__*/_jsx(Text, {
      style: selectTextStyles(themeTokens),
      accessibilityLabel: subPanelAccessibilityLabel,
      children: children
    }) : children
  }) : /*#__PURE__*/_jsxs(View, {
    ref: ref,
    style: [themeTokens, staticStyles.container],
    accessibilityLabel: panelAccessibilityLabel,
    children: [/*#__PURE__*/_jsx(View, {
      style: selectControlPanelStyles(themeTokens),
      children: /*#__PURE__*/_jsx(ExpandCollapseControl, {
        ...selectedProps,
        isExpanded: isExpanded,
        tokens: controlTokens,
        variant: variant,
        onPress: handleControlPress,
        ref: controlRef,
        children: control
      })
    }), isExpanded && /*#__PURE__*/_jsx(View, {
      style: {
        borderTopColor: themeTokens.expandDividerColor,
        borderTopWidth: themeTokens.expandDividerWidth,
        marginLeft: themeTokens.contentPaddingLeft,
        marginRight: themeTokens.contentPaddingRight
      }
    }), /*#__PURE__*/_jsx(Animated.ScrollView, {
      ref: animatedRef,
      style: animatedStyles,
      ...focusabilityProps,
      children: /*#__PURE__*/_jsx(View, {
        onLayout: onContainerLayout,
        style: {
          ...Platform.select({
            default: {
              flex: 1,
              overflow: 'hidden'
            },
            web: {
              overflow: 'hidden'
            }
          })
        },
        children: Platform.OS === 'web' ? /*#__PURE__*/_jsx(View, {
          style: selectContainerStyles(themeTokens),
          accessibilityLabel: subPanelAccessibilityLabel,
          children: children
        }) : /*#__PURE__*/_jsx(ScrollView, {
          onContentSizeChange: (_, height) => {
            setContainerHeight(height + mobileScrollBuffer);
          },
          style: selectContainerStyles(themeTokens),
          accessibilityLabel: subPanelAccessibilityLabel,
          children: children
        })
      })
    })]
  });
});
ExpandCollapsePanel.displayName = 'ExpandCollapsePanel';
const staticStyles = StyleSheet.create({
  container: {
    ...Platform.select({
      web: {
        flexGrow: 1,
        flexshrink: 1,
        flexbasis: 'auto'
      },
      default: {
        flex: 1
      }
    }),
    justifyContent: 'flex-start'
  },
  panelContainer: {
    flexGrow: 1,
    alignSelf: 'stretch'
  }
});
ExpandCollapsePanel.propTypes = {
  ...selectedSystemPropTypes,
  variant: variantProp.propType,
  tokens: getTokensPropType('ExpandCollapsePanel'),
  /**
   * Array of the ids of currently open groups
   */
  openIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
  /**
   * Unique name by which the ExpandCollapse controls this element.
   */
  panelId: PropTypes.string.isRequired,
  /**
   * Function to call on pressing the panel's control, which should open or close the panel.
   * If Panel is a direct child of `ExpandCollapse`, this prop will be provided by the ExpandCollapse parent.
   */
  onToggle: PropTypes.func,
  /**
   * Optional function to call on pressing the panel's control, in addition to opening or closing the panel.
   */
  onPress: PropTypes.func,
  /**
   * ExpandCollapsePanel's children are the content that is hidden or revealed on open or close, and may be any
   * single React element or a render function which takes this group's isExpanded state, onToggle function and panelId
   */
  children: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
  /**
   * The content inside the control element that opens and closes the ExpandCollapse when pressed.
   */
  control: ExpandCollapseControl.propTypes.children,
  /**
   * Optional theme token overrides that may be passed to the ExpandCollapseControl element.
   */
  controlTokens: getTokensPropType('ExpandCollapseControl'),
  /**
   * An optional ref to be attached to the control
   */
  controlRef: ABBPropTypes.ref(),
  /**
   * A boolean prop to determine if the panel is a content panel or not. If true, the panel will not have a control
   */
  content: PropTypes.bool
};
export default ExpandCollapsePanel;